import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import type { FlowComponentConfig } from '../types';

export function FeedbackRecoveryContent({
  components,
  renderBlock,
  componentKey,
  surface,
  border,
  spacing,
}: {
  components: FlowComponentConfig[];
  renderBlock: (component: FlowComponentConfig) => ReactNode;
  componentKey: (component: FlowComponentConfig) => string;
  surface: string;
  border: string;
  spacing: number;
}) {
  const content: ReactNode[] = [];
  for (let index = 0; index < components.length; index += 1) {
    const art = components[index];
    const heading = components[index + 1];
    const body = components[index + 2];
    const action = components[index + 3];
    if (
      art.type === 'visualAsset' &&
      art.preset === 'recovery_contact' &&
      !art.url &&
      !art.animation &&
      heading?.type === 'h2' &&
      body?.type === 'subheader' &&
      action?.type === 'actionButton'
    ) {
      const groupedComponents = [heading, body, action];
      const nodes = groupedComponents.map((component) => renderBlock(component));
      // The stock recovery illustration is decorative. Keep authored recovery
      // copy and actions compact while custom visual assets continue to render.
      if (nodes.every((node) => node !== null && node !== undefined && node !== false)) {
        content.push(
          <Stack
            key={componentKey(art)}
            data-feedback-recovery-card
            spacing={0.75}
            sx={{
              border: `1px solid ${border}`,
              borderRadius: '6px',
              p: 1.25,
              bgcolor: surface,
            }}
          >
            <Stack spacing="2px">
              <Box
                sx={
                  !heading.size || heading.size === 'standard'
                    ? {
                        '& h2': {
                          fontSize: 16,
                          lineHeight: '24px',
                          fontWeight: 600,
                          letterSpacing: 0,
                        },
                      }
                    : undefined
                }
              >
                {nodes[0]}
              </Box>
              <Box
                sx={
                  !body.size || body.size === 'standard'
                    ? {
                        '& [data-flow-component="subheader"] > *': {
                          fontSize: 12,
                          lineHeight: '18px',
                          letterSpacing: 0,
                          color: 'var(--cx-brand-text)',
                        },
                      }
                    : undefined
                }
              >
                {nodes[1]}
              </Box>
            </Stack>
            <Box>{nodes[2]}</Box>
          </Stack>
        );
        index += 3;
        continue;
      }
      nodes.forEach((node, offset) => {
        if (node !== null && node !== undefined && node !== false)
          content.push(<Box key={componentKey(groupedComponents[offset])}>{node}</Box>);
      });
      index += 3;
    } else {
      const node = renderBlock(art);
      if (node !== null && node !== undefined && node !== false)
        content.push(
          <Box
            key={componentKey(art)}
            sx={
              art.type === 'reviewSelection'
                ? {
                    '& [data-feedback-review-title]': {
                      fontSize: 14,
                      lineHeight: '22px',
                      fontWeight: 600,
                    },
                  }
                : undefined
            }
          >
            {node}
          </Box>
        );
    }
  }
  return <Stack spacing={spacing}>{content}</Stack>;
}
